// client/components/ui/FilterBarOrDrawer.jsx
import React, { useEffect, useId, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { BREAKPOINTS, useViewportWidth } from "../../hooks/useResponsive.js";
import { Z_INDEX } from "./zIndex.js";
import { X, Filter } from "lucide-react";
import AppButton from "./AppButton.jsx";
import { mobileFilterDrawerStackClassName } from "../../design/tokens.js";
import { FilterDrawerModeContext } from "./filterDrawerContext.js";
import { acquireBodyScrollLock } from "../../utils/bodyScrollLock.js";

/**
 * FilterBarOrDrawer - Responsive Filter-Komponente
 *
 * Unterhalb eines Schwellenwerts: Filter-Button + Bottom-Sheet mit den Children.
 * Darüber: Children inline als Filterleiste (flex-wrap).
 *
 * Props:
 * - children - Filter-Inhalt
 * - title - Sheet-Titel
 * - className - Klassen am Inline-Wrapper bzw. am Filter-Button (Mobile-Pfad)
 * - drawerUntil: 'md' | 'lg' | 'xl' — bis zu diesem Breakpoint Sheet statt Inline (Standard `md`).
 *   Listen mit `xl:flex-row` sollten oft `xl` verwenden, sonst 768–1279px zerquetschen die Filter.
 */
export default function FilterBarOrDrawer({
  children,
  title = "Filter",
  className = "",
  drawerUntil = "md",
}) {
  const width = useViewportWidth();
  const prefersDrawerLayout = useMemo(() => {
    const th =
      drawerUntil === "xl"
        ? BREAKPOINTS.xl
        : drawerUntil === "lg"
          ? BREAKPOINTS.lg
          : BREAKPOINTS.md;
    return width < th;
  }, [width, drawerUntil]);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [frozenDrawerMode, setFrozenDrawerMode] = useState(prefersDrawerLayout);
  const titleId = useId();
  const drawerChildren = React.Children.toArray(children);

  useEffect(() => {
    if (drawerOpen) return;
    setFrozenDrawerMode(prefersDrawerLayout);
  }, [prefersDrawerLayout, drawerOpen]);

  useEffect(() => {
    if (!drawerOpen) return;
    const release = acquireBodyScrollLock();
    return release;
  }, [drawerOpen]);

  const showDrawerChrome = drawerOpen ? frozenDrawerMode : prefersDrawerLayout;

  if (!showDrawerChrome) {
    return <div className={`flex flex-wrap items-center gap-2 ${className}`}>{children}</div>;
  }

  return (
    <>
      <AppButton
        type="button"
        variant="secondary"
        onClick={() => {
          setFrozenDrawerMode(prefersDrawerLayout);
          setDrawerOpen(true);
        }}
        className={`flex items-center gap-2 px-4 py-2.5 min-h-[44px] font-normal ${className}`}
        aria-label="Filter öffnen"
      >
        <Filter className="w-4 h-4" />
        <span>Filter</span>
      </AppButton>

      {drawerOpen &&
        createPortal(
          <div className="fixed inset-0" style={{ zIndex: Z_INDEX.sheet }} aria-modal="true" role="dialog" aria-labelledby={titleId}>
            <div
              className={`absolute inset-0 backdrop-blur-sm transition-opacity ${"bg-black/60"}`}
              onClick={() => setDrawerOpen(false)}
              aria-hidden="true"
            />

            <div
              className={`absolute left-0 right-0 bottom-0 border-t rounded-t-2xl shadow-2xl flex flex-col transition-all ${"bg-surface-elevated border-default"}`}
              style={{
                maxHeight: "calc(100dvh - 12px)",
                paddingBottom: "max(12px, env(safe-area-inset-bottom))",
              }}
              onMouseDown={(e) => e.stopPropagation()}
            >
              <div className={`sticky top-0 px-4 pt-3 pb-2 backdrop-blur rounded-t-2xl border-b z-10 ${"bg-surface-elevated border-default"}`}>
                <div className={`mx-auto h-1.5 w-10 rounded-full mb-2 ${"bg-white/20"}`} aria-hidden="true" />
                <div className="flex items-center gap-2">
                  <h2 id={titleId} className={`flex-1 text-base font-semibold ${"text-primary"}`}>
                    {title}
                  </h2>
                  <AppButton
                    type="button"
                    variant="ghost"
                    onClick={() => setDrawerOpen(false)}
                    className={`!h-11 !w-11 !min-h-[44px] !min-w-[44px] !p-0 rounded-lg font-normal ${"text-secondary"}`}
                    aria-label="Schließen"
                    title="Schließen"
                  >
                    <X className="w-5 h-5" />
                  </AppButton>
                </div>
              </div>

              <div className="px-4 pb-4 overflow-auto flex-1 overscroll-contain min-h-0" style={{ WebkitOverflowScrolling: "touch" }}>
                <FilterDrawerModeContext.Provider value={true}>
                  <div className={mobileFilterDrawerStackClassName}>
                    {drawerChildren.map((child, idx) => (
                      <div key={idx} className="w-full min-w-0 [&_button]:max-md:min-h-[44px] [&_select]:max-md:min-h-[44px] [&_input]:max-md:min-h-[44px]">
                        {child}
                      </div>
                    ))}
                  </div>
                </FilterDrawerModeContext.Provider>
              </div>
            </div>
          </div>,
          document.body,
        )}
    </>
  );
}
